import { Subscription } from 'rxjs';

import { FeatureName, SupportService } from '../support/support.service';
import { LoggingService } from '../logging/logging.service';
import { NotificationService } from '../notification/notification.service';
import { ParticipantsListService } from '../participants-list/participants-list.service';

import { MicStream } from '../../models/mic-stream/mic-stream';
import { Participant } from '../../models/participant/participant';
import { VolumeStreamEvents } from '../../models/volume-stream-events/volume-stream-events';

export interface VolumeMonitorState {
  micStream: MicStream;
  fullStream: MediaStream;
  audioSwitch: boolean;
  userId: string;
}

export class VolumeMonitor {
  private mediaStreamSource: MediaStreamAudioSourceNode;
  private makingNoise: number;
  private volumeSubscription: Subscription;
  private timeRefresh: number;
  private handlerId = 'stream-service';

  constructor(
    private support: SupportService,
    private logging: LoggingService,
    private notification: NotificationService,
    private participantsList: ParticipantsListService,
    private getState: () => VolumeMonitorState,
    private setTalkingOnMute: (value: boolean) => void,
    private setMainStreamCb: () => void,
  ) {
    this.makingNoise = 0;
    this.timeRefresh = Date.now();
  }

  loadMyVolume(): void {
    const state = this.getState();
    if (this.support.isFeatureSupported(FeatureName.AudioVolume) && state.micStream) {
      state.micStream.load();
      state.micStream.subscribeHandler(this.handlerId, (event) => this.handleVolumeStreamEvents(event));
      this.subscribeToVolumeObserver();
    }
  }

  setupMyVolume(): void {
    this.loadMyVolume();
    this.connectVolumeSource();
  }

  unloadMyVolume(): void {
    this.disconnectVolumeSource();
    const state = this.getState();
    if (state.micStream) {
      this.clearSubscription();
      state.micStream.unsubscribeHandler(this.handlerId);
      state.micStream.unload();
    }
  }

  refreshVolume(id: string, volume: number, timestamp: number, remoteTimeMakingNoise?: number): void {
    const collaborationMode = document.getElementById('presentation-video')?.hidden;
    const timeNow = Date.now();
    const state = this.getState();
    const participant = this.participantsList.participants[id];

    if (!participant) {
      return;
    }

    participant.volume = volume;

    if (state.userId !== id) {
      participant.lastSpokeTimestamp = timestamp;
      participant.timeMakingNoise = remoteTimeMakingNoise;
      if (participant.timeMakingNoise > 1) {
        participant.isActive = true;
      }
      if (participant.timeMakingNoise === 0) {
        participant.isActive = false;
      }
    } else {
      if (volume > 0.01) {
        participant.stopMakingNoise = timeNow;
        participant.timeMakingNoise += 1;
        if (participant.timeMakingNoise > 1) {
          participant.lastSpokeTimestamp = timestamp;
          participant.isActive = true;
        }
      } else if (volume < 0.005) {
        if ((timeNow - participant.stopMakingNoise) > 1500) {
          participant.timeMakingNoise = 0;
          participant.isActive = false;
        }
      }
    }

    if (timeNow - this.timeRefresh > 1000 && !collaborationMode) {
      this.setMainStreamCb();
      this.timeRefresh = timeNow;
    }
  }

  connectVolumeSource(): void {
    const state = this.getState();
    if (this.support.isFeatureSupported(FeatureName.AudioVolume)) {
      state.micStream.connect();
    }
  }

  disconnectVolumeSource(): void {
    const state = this.getState();
    if (this.support.isFeatureSupported(FeatureName.AudioVolume) && state.micStream) {
      state.micStream.disconnect();
    }
    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }
  }

  clearMediaStreamSource(): void {
    this.mediaStreamSource = null;
  }

  private onMicVolume(volume: number): void {
    const state = this.getState();
    if (!state.audioSwitch) {
      if (volume > 0.01) {
        this.makingNoise += 1;
        if (this.makingNoise > 5) {
          this.setTalkingOnMute(true);
          this.notification.display('room.talking_on_mute_message', 'danger', 'top', 2000, [], 'mic-off-toast');
        }
      } else if (volume < 0.005) {
        this.setTalkingOnMute(false);
        this.makingNoise = 0;
      }
    } else {
      this.setTalkingOnMute(false);
      this.makingNoise = 0;
    }
  }

  private handleVolumeStreamEvents(event: string): void {
    switch (event) {
      case VolumeStreamEvents.Connected:
        return this.subscribeToVolumeObserver();
      case VolumeStreamEvents.Disconnected:
        this.clearSubscription();
        return;
      case VolumeStreamEvents.Restarted:
        this.clearSubscription();
        return this.subscribeToVolumeObserver();
    }
  }

  private subscribeToVolumeObserver(): void {
    const state = this.getState();
    if (!state.micStream.connected) {
      return;
    }

    if (this.volumeSubscription) {
      return;
    }

    state.micStream.getVolumePromise().then(() => {
      const currentState = this.getState();
      this.volumeSubscription = currentState.micStream.getUnbufferedVolumeObserver().subscribe(volume => {
        try {
          this.onMicVolume(volume);
          const latestState = this.getState();
          this.refreshVolume(latestState.userId, latestState.audioSwitch && volume || 0, Date.now());
        } catch (err) {
          this.logging.error('StreamService: Error processing volume inputs', volume, err);
        }
      });
    });
  }

  private clearSubscription(): void {
    if (this.volumeSubscription) {
      this.volumeSubscription.unsubscribe();
      this.volumeSubscription = null;
    }
  }
}
